import React, { useState } from 'react';
import ActionSheet from '../ui/ActionSheet';
import HistorialEquipoModal from '../equipo/HistorialEquipoModal';
import { abrirMaps, abrirWhatsApp, resumenCliente, formatFecha, sedesQueCoinciden, direccionSede, linkMaps } from '../../utils/clienteUtils';
import { coincideTodo } from '../../utils/busqueda';
import HistorialClienteModal from './HistorialClienteModal';
import CierreMensualModal from './CierreMensualModal';
import FuncionesPortalModal from './FuncionesPortalModal';
import RentabilidadCliente from '../rentabilidad/RentabilidadCliente';
import EmpresaPortalSheet from './EmpresaPortalSheet';
import { LuMapPin, LuMessageCircle, LuWrench, LuShoppingCart, LuPencil, LuClipboardList, LuTrash2, LuHouse, LuTriangleAlert, LuBuilding2, LuUser, LuFileText, LuSlidersHorizontal, LuNavigation, LuSearch, LuChartColumn, LuChevronUp } from 'react-icons/lu';
import DireccionMapa from '../ui/DireccionMapa';
import NuevoPedidoSheet from '../empresa/NuevoPedidoSheet';
import ChipFiltro from '../ui/ChipFiltro';
import FiltrosPanel from '../ui/FiltrosPanel';
import { useFiltros } from '../../hooks/useFiltros';
import { periodoLabelDe } from '../../utils/dateUtils';

export default function ClienteCard({
    cliente, sedes, equipos, servicios = [],
    isExpanded, onToggleExpand,
    onEditCliente, onDeleteCliente,
    onEditEquipo, onArchivarEquipo, onRestaurarEquipo, onEliminarEquipoDefinitivo,
    onAddSede, onAddEquipo,
    onNuevoServicio, onNuevaVenta,
    enModal = false, sedeFoco = null, busqueda = '',
}) {
    const [equipoHistorial, setEquipoHistorial] = useState(null);
    const [modalHistorial, setModalHistorial] = useState(false);
    const [modalCierre, setModalCierre] = useState(false);
    const [modalPortal, setModalPortal] = useState(false);
    const [modalRentab, setModalRentab] = useState(false);
    const [modalEmpresa, setModalEmpresa] = useState(false);
    const [menuCliente, setMenuCliente] = useState(false);
    const [pedido, setPedido] = useState(false);
    const [menuEquipo, setMenuEquipo] = useState(null); // id del equipo con menú abierto
    const [confirmEliminar, setConfirmEliminar] = useState(null); // 'cliente' | equipo (objeto)

    const sedesCli       = sedes.filter(s => (s.clienteId || s.cliente?.id) === cliente.id);
    // 28-oct-2026: con muchas sedes (Modo Agua tiene 52), se abre en la que buscaste y se puede buscar adentro
    const [soloSede, setSoloSede] = useState(sedeFoco);
    const [filtroSede, setFiltroSede] = useState(() => (!sedeFoco && sedesQueCoinciden(cliente, sedes, busqueda).length ? busqueda : ''));
    const sedesVer = soloSede ? sedesCli.filter(s => s.id === soloSede)
        : filtroSede.trim() ? sedesCli.filter(s => coincideTodo([s.codigo, s.nombreSede, direccionSede(s)].filter(Boolean).join(' '), filtroSede))
            : sedesCli;
    // 28-oct-2026: cliente grande (Modo Agua va a tener 900+ equipos): lista compacta, un renglón por lugar,
    // ordenada por el último service, de a 15, con "+ Lugar" arriba. Se abre un lugar para ver sus equipos.
    const grande = sedesCli.length > 8;
    const [orden, setOrden] = useState('reciente');   // reciente | antiguo | codigo
    const [cuantos, setCuantos] = useState(15);
    const [verFiltroFecha, setVerFiltroFecha] = useState(false);
    const [abiertaSede, setAbiertaSede] = useState(sedeFoco || null);
    const sedeIds        = sedesCli.map(s => String(s.id));
    const eqCli          = equipos.filter(eq => sedeIds.includes(String(eq.sedeId)));
    const equiposActivos = eqCli.filter(eq => eq.activo !== false);

    // Cálculos compartidos con ClienteRow (desktop) — centralizados en
    // clienteUtils.js para que las dos vistas no puedan desincronizarse.
    const {
        serviciosCli, ultimoServicio, diasSinAtender, alertaSinServicio,
        esEmpresa, tieneTecnica, tieneVenta, iniciales,
    } = resumenCliente(cliente, sedes, equipos, servicios);

    // Último service de cada equipo (para mostrarlo en la ficha)
    const ultimoPorEquipo = {};
    serviciosCli.forEach(s => (s.items || []).forEach(it => {
        if (it.equipoId && !ultimoPorEquipo[it.equipoId]) ultimoPorEquipo[it.equipoId] = s.fecha;
    }));
    // Último service de cada lugar (por el lugar del trabajo o por sus equipos) y la lista ordenada
    const eqASede = Object.fromEntries(equiposActivos.map(eq => [eq.id, String(eq.sedeId)]));
    const ultimoPorSede = {};
    const anotar = (sid, f) => { if (sid && f && (!ultimoPorSede[sid] || String(f) > String(ultimoPorSede[sid]))) ultimoPorSede[sid] = f; };
    serviciosCli.forEach(sv => {
        anotar(sv.sedeId != null ? String(sv.sedeId) : null, sv.fecha);
        (sv.items || []).forEach(it => anotar(eqASede[it.equipoId], sv.fecha));
    });
    const ultimoDeSede = (sede) => ultimoPorSede[String(sede.id)] || null;
    // Filtro de fechas (el mismo de toda la app): por la fecha del último service del lugar
    const filtroFechas = useFiltros(sedesVer.map(sd => ({ ...sd, fecha: ultimoDeSede(sd) })), { periodoInicial: 'TODO' });
    const sedesOrdenadas = [...filtroFechas.itemsFiltrados].sort((x, y) => {
        if (orden === 'az') return String(x.nombreSede || x.codigo || '').localeCompare(String(y.nombreSede || y.codigo || ''), 'es', { sensitivity: 'base' });
        if (orden === 'codigo') return String(x.codigo || x.nombreSede || '').localeCompare(String(y.codigo || y.nombreSede || ''), undefined, { numeric: true });
        const fx = String(ultimoDeSede(x) || ''), fy = String(ultimoDeSede(y) || '');
        return orden === 'antiguo' ? fx.localeCompare(fy) : fy.localeCompare(fx);
    });
    const IVA_LABEL = { CONSUMIDOR_FINAL: 'Consumidor final', RESPONSABLE_INSCRIPTO: 'Responsable inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'Exento' };

    // Dirección formateada
    const direccion = [cliente.calle, cliente.numero, cliente.localidad].filter(Boolean).join(' ');

    // Vista colapsada — Opción B del rediseño (Lucas, 7-sep-2026): sin borde
    // de color aparte, avatar redondo con un pin de tipo (empresa/particular,
    // reemplaza el chip de texto "Empresa") y el estado en un punto de color
    // junto a la fecha en vez de una franja/badge aparte. El pin de abajo a
    // la derecha (tieneTecnica/tieneVenta) se deja como estaba.
    if (!isExpanded) {
        const pinCls = 'absolute -bottom-1 -right-1 flex items-center justify-center gap-0.5 rounded-full bg-ink text-white dark:text-[#1C1917] border-2 border-card';
        const PinTipo = esEmpresa ? LuBuilding2 : LuUser;
        const dotColor = alertaSinServicio ? 'bg-brand-amber' : (ultimoServicio ? 'bg-brand-green' : 'bg-muted');

        return (
            <div onClick={onToggleExpand}
                className="bg-card rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-all border border-black/[0.07] dark:border-white/[0.07] px-3 py-2.5 flex items-center gap-3">
                <span className="relative w-10 h-10 rounded-full bg-chip text-ink flex items-center justify-center font-black text-label shrink-0">
                    {iniciales}
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-panel border-2 border-card flex items-center justify-center text-muted"
                        title={esEmpresa ? 'Empresa' : 'Particular'}>
                        <PinTipo size={8} />
                    </span>
                    {(tieneTecnica || tieneVenta) && (
                        <span className={`${pinCls} ${tieneTecnica && tieneVenta ? 'h-4 px-1' : 'w-4 h-4'}`}
                            title={tieneTecnica && tieneVenta ? 'Servicio técnico y venta' : tieneTecnica ? 'Servicio técnico' : 'Venta'}>
                            {tieneTecnica && <LuWrench size={9} />}
                            {tieneVenta && <LuShoppingCart size={9} />}
                        </span>
                    )}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-body font-black text-ink leading-tight truncate flex items-center gap-1.5">
                        <span className="truncate">{cliente.nombre}</span>
                        {cliente.tieneTarifa && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[color:var(--etapa-cobrar-bg)] text-[color:var(--etapa-cobrar-tx)] text-label font-black">Tarifa mensual</span>
                        )}
                    </p>
                    <p className="text-caption text-muted mt-0.5 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
                        <span className="truncate">
                            {ultimoServicio ? `Últ: ${formatFecha(ultimoServicio.fecha)}` : 'Sin servicios'}
                            {alertaSinServicio && (
                                <span className="text-brand-amber font-bold"> · {diasSinAtender} días sin visita</span>
                            )}
                        </span>
                    </p>
                </div>
                {cliente.telefono && (
                    <button
                        onClick={(e) => { e.stopPropagation(); abrirWhatsApp(cliente.telefono, cliente.nombre); }}
                        title="WhatsApp"
                        aria-label={`WhatsApp a ${cliente.nombre}`}
                        className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#25D366] text-white shrink-0 active:scale-90 transition-all">
                        <LuMessageCircle size={18} />
                    </button>
                )}
            </div>
        );
    }

    // Vista expandida (dentro del overlay modal)
    return (
        <div className={enModal ? '' : 'bg-card rounded-2xl border border-black/[0.07] dark:border-white/[0.07] overflow-hidden'}>
            <div className="space-y-4">

                {/* Dirección clickeable */}
                {direccion && direccion !== 'Sin dirección 0' && (
                    <DireccionMapa direccion={direccion} />
                )}

                {/* Acciones principales */}
                <div className="flex gap-2">
                    {cliente.telefono && (
                        <button onClick={() => abrirWhatsApp(cliente.telefono, cliente.nombre)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#25D366] text-white rounded-xl font-black text-label active:scale-95 transition-all">
                            <LuMessageCircle size={14} /> WA
                        </button>
                    )}
                    {/* 1-nov-2026: empresa con cobro mensual: lo de todos los días es un pedido; Servicio y Venta van al ⋯ */}
                    {cliente.tieneTarifa && (
                        <button onClick={() => setPedido(true)}
                            className="flex-[2] flex items-center justify-center gap-1 py-2.5 bg-[#C9341F] text-white rounded-xl font-black text-label active:scale-95 transition-all">
                            <LuClipboardList size={14} /> Nuevo pedido
                        </button>
                    )}
                    {onNuevoServicio && !cliente.tieneTarifa && (
                        <button onClick={() => onNuevoServicio(cliente)}
                            className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-chip text-ink rounded-xl font-black text-label active:scale-95 transition-all">
                            <LuWrench size={14} /> Servicio
                        </button>
                    )}
                    {onNuevaVenta && !cliente.tieneTarifa && (
                        <button onClick={() => onNuevaVenta(cliente)}
                            className="flex-1 flex items-center justify-center gap-1 py-2.5 bg-chip text-ink rounded-xl font-black text-label active:scale-95 transition-all">
                            <LuShoppingCart size={14} /> Venta
                        </button>
                    )}
                    {/* Menú ⋯ del cliente */}
                    <div className="relative">
                        <button onClick={() => setMenuCliente(v => !v)}
                            className="w-10 h-10 rounded-xl flex items-center justify-center bg-chip text-muted active:scale-95">⋯</button>
                        <ActionSheet open={menuCliente} onClose={() => setMenuCliente(false)}>
                                    {cliente.tieneTarifa && onNuevoServicio && (
                                        <button onClick={() => { onNuevoServicio(cliente); setMenuCliente(false); }}
                                            className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                            <LuWrench size={15} /> Presupuesto aparte (con precio)
                                        </button>
                                    )}
                                    {cliente.tieneTarifa && onNuevaVenta && (
                                        <button onClick={() => { onNuevaVenta(cliente); setMenuCliente(false); }}
                                            className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                            <LuShoppingCart size={15} /> Venta
                                        </button>
                                    )}
                                    <button onClick={() => { onEditCliente(cliente); setMenuCliente(false); }}
                                        className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                        <LuPencil size={15} /> Editar cliente
                                    </button>
                                    <button onClick={() => { setModalHistorial(true); setMenuCliente(false); }}
                                        className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                        <LuClipboardList size={15} /> Ver historial
                                    </button>
                                    <button onClick={() => { setModalCierre(true); setMenuCliente(false); }}
                                        className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                        <LuFileText size={15} /> {cliente.tieneTarifa ? 'Cierre mensual' : 'Configurar tarifa mensual'}
                                    </button>
                                    {/* 28-oct-2026: todo lo de "empresa como Modo Agua" en un lugar (tarifa, usuarios del portal, funciones) */}
                                    <button onClick={() => { setModalEmpresa(true); setMenuCliente(false); }}
                                        className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                        <LuSlidersHorizontal size={15} /> Empresa con portal
                                    </button>
                                    <button onClick={() => { abrirMaps(cliente); setMenuCliente(false); }}
                                        className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                        <LuMapPin size={15} /> Ver en mapa
                                    </button>
                                    {/* 1-nov-2026: mirando un solo lugar (se llegó buscándolo), "Eliminar cliente" borraba el cliente
                                        entero con todos sus lugares (se borró Modo Agua buscando "Escuela 66"). Ahí no se ofrece. */}
                                    {!soloSede && (
                                        <button onClick={() => { setConfirmEliminar('cliente'); setMenuCliente(false); }}
                                            className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-brand-red active:bg-[#FEE2E2] rounded-xl flex items-center gap-2.5">
                                            <LuTrash2 size={15} /> Eliminar cliente entero
                                        </button>
                                    )}
                        </ActionSheet>
                    </div>
                </div>

                {/* Datos fiscales y de contacto, a la vista */}
                {(cliente.cuilDni || cliente.condicionIva || cliente.telefono || cliente.email) && (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2 px-3 py-2.5 rounded-xl bg-panel text-caption">
                        {[['CUIT / DNI', cliente.cuilDni], ['IVA', IVA_LABEL[cliente.condicionIva] || cliente.condicionIva],
                          ['Teléfono', cliente.telefono], ['Email', cliente.email]].filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="min-w-0">
                                <p className="text-label font-bold text-muted uppercase tracking-wide">{k}</p>
                                <p className="font-bold text-ink truncate select-all">{v}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Clientes con tarifa: el cierre del mes a mano */}
                {cliente.tieneTarifa && (
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setModalCierre(true)}
                            className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[color:var(--etapa-cobrar-bg)] text-left active:scale-[0.99]">
                            <span className="text-caption font-black text-[color:var(--etapa-cobrar-tx)] flex items-center gap-1.5"><LuFileText size={14} /> Cierre mensual</span>
                            <span className="text-label text-muted">→</span>
                        </button>
                        {/* 28-oct-2026: antes era una sección aparte del menú */}
                        <button onClick={() => setModalRentab(true)}
                            className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-panel text-left active:scale-[0.99]">
                            <span className="text-caption font-black text-ink flex items-center gap-1.5"><LuChartColumn size={14} /> Rentabilidad</span>
                            <span className="text-label text-muted">→</span>
                        </button>
                    </div>
                )}

                {/* Sedes + Equipos */}
                <div className="space-y-2">
                    {grande && !soloSede && (
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-caption font-black text-ink">{sedesCli.length} lugares · {equiposActivos.length} equipos</p>
                            <button onClick={() => onAddSede(cliente)} className="h-9 px-3 rounded-xl bg-chip text-ink text-label font-black active:scale-95">+ Lugar</button>
                        </div>
                    )}
                    {sedesCli.length > 5 && (soloSede ? (
                        <button type="button" onClick={() => setSoloSede(null)}
                            className="w-full h-10 rounded-xl bg-chip text-ink text-label font-black active:scale-95">Ver los {sedesCli.length} lugares</button>
                    ) : (
                        <label className="flex items-center gap-2 h-11 px-3 rounded-xl bg-page border border-black/10 dark:border-white/10">
                            <LuSearch size={15} className="text-muted shrink-0" />
                            <input value={filtroSede} onChange={e => setFiltroSede(e.target.value)} placeholder={`Buscar entre ${sedesCli.length} lugares (nombre, código, calle)`}
                                className="flex-1 min-w-0 bg-transparent outline-none text-body text-ink placeholder:text-muted" />
                            {filtroSede && <button type="button" onClick={() => setFiltroSede('')} className="text-caption font-black text-muted">✕</button>}
                        </label>
                    ))}
                    {grande && !soloSede && (
                        <div className="flex gap-1.5">
                            <select value={orden} onChange={e => { setOrden(e.target.value); setCuantos(15); }} aria-label="Ordenar"
                                className="h-9 px-2.5 rounded-lg bg-card text-secondary text-label font-bold outline-none shadow-sm border border-black/[0.05] dark:border-white/[0.05]">
                                <option value="reciente">Últimos atendidos</option>
                                <option value="antiguo">Hace más tiempo</option>
                                <option value="az">A → Z</option>
                                <option value="codigo">Por código</option>
                            </select>
                            <ChipFiltro label={filtroFechas.periodoRapido === 'TODO' && !filtroFechas.mesSelector ? 'Atendidos: todo' : `Atendidos: ${periodoLabelDe(filtroFechas)}`}
                                activo={verFiltroFecha} onClick={() => setVerFiltroFecha(v => !v)} />
                            <span className="ml-auto self-center text-label font-bold text-muted">{sedesOrdenadas.length}</span>
                        </div>
                    )}
                    {grande && !soloSede && verFiltroFecha && <FiltrosPanel hook={filtroFechas} conBusqueda={false} conRango />}
                    {!soloSede && filtroSede.trim() && sedesVer.length === 0 && <p className="px-1 text-caption text-muted">Ningún lugar coincide</p>}
                    {(grande && !soloSede ? sedesOrdenadas.slice(0, cuantos) : sedesVer).map(sede => {
                        const eqSede = equiposActivos.filter(eq => String(eq.sedeId) === String(sede.id));
                        if (grande && !soloSede && abiertaSede !== sede.id) {
                            const ult = ultimoDeSede(sede);
                            return (
                                <button key={sede.id} type="button" onClick={() => setAbiertaSede(sede.id)}
                                    className="w-full px-3 py-2 rounded-xl bg-panel flex items-center gap-2 text-left active:scale-[0.99]">
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-caption font-black text-ink truncate">{[sede.codigo, sede.nombreSede].filter(Boolean).join(' · ')}</span>
                                        <span className="block text-label text-muted truncate">{[direccionSede(sede), `${eqSede.length} eq`].filter(Boolean).join(' · ')}</span>
                                    </span>
                                    <span className="shrink-0 text-label font-bold text-muted">{ult ? formatFecha(ult) : 'Sin service'}</span>
                                </button>
                            );
                        }
                        return (
                            <div key={sede.id} className="rounded-xl bg-panel overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2">
                                    <div className="min-w-0">
                                        <p className="text-caption font-black text-ink">
                                            <LuHouse size={12} className="inline mr-1 -mt-0.5" />{[sede.codigo, sede.nombreSede].filter(Boolean).join(' · ')}
                                            {eqSede.length > 0 && (
                                                <span className="text-muted font-bold ml-1.5">{eqSede.length} eq</span>
                                            )}
                                        </p>
                                        {sedesCli.length > 1 && direccionSede(sede) && (
                                            <a href={linkMaps(direccionSede(sede))} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 mt-0.5 text-caption font-bold text-secondary underline active:opacity-60">
                                                <LuNavigation size={11} className="shrink-0" />{direccionSede(sede)}
                                            </a>
                                        )}
                                    </div>
                                    <span className="flex items-center gap-3 shrink-0">
                                        <button onClick={() => onAddEquipo(cliente)}
                                            className="text-label font-black text-brand-red active:scale-95">
                                            + EQUIPO
                                        </button>
                                        {grande && !soloSede && (
                                            <button type="button" onClick={() => setAbiertaSede(null)} aria-label="Cerrar el lugar"
                                                className="w-8 h-8 rounded-lg bg-chip text-muted inline-flex items-center justify-center active:scale-90"><LuChevronUp size={15} /></button>
                                        )}
                                    </span>
                                </div>
                                {eqSede.length > 0 && (
                                    <div className="border-t border-black/[0.05] dark:border-white/[0.05]">
                                        {eqSede.map(eq => (
                                            <div key={eq.id} className="flex items-center gap-2 px-3 py-2 border-b border-black/[0.04] dark:border-white/[0.04] last:border-0">
                                                <button type="button" onClick={() => setEquipoHistorial(eq)} className="flex-1 min-w-0 text-left">
                                                    <p className="text-caption font-bold text-ink truncate">
                                                        {eq.modelo || 'Equipo'} <span className="text-muted">· {eq.numeroSerie}</span>
                                                    </p>
                                                    <p className="text-caption text-muted truncate">
                                                        {[eq.ubicacion, ultimoPorEquipo[eq.id] ? `Último service ${formatFecha(ultimoPorEquipo[eq.id])}` : 'Sin services'].filter(Boolean).join(' · ')}
                                                    </p>
                                                </button>
                                                <div className="relative">
                                                    <button onClick={() => setMenuEquipo(menuEquipo === eq.id ? null : eq.id)}
                                                        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted active:scale-90 text-label">⋯</button>
                                                    <ActionSheet open={menuEquipo === eq.id} onClose={() => setMenuEquipo(null)}>
                                                                <p className="px-5 py-2 text-label font-black text-muted truncate">{eq.modelo || eq.numeroSerie}</p>
                                                                <button onClick={() => { setEquipoHistorial(eq); setMenuEquipo(null); }}
                                                                    className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                                                    <LuClipboardList size={15} /> Historial
                                                                </button>
                                                                <button onClick={() => { onEditEquipo(eq, cliente); setMenuEquipo(null); }}
                                                                    className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-ink active:bg-[#E8E5E0] rounded-xl flex items-center gap-2.5">
                                                                    <LuPencil size={15} /> Editar
                                                                </button>
                                                                <button onClick={() => { setConfirmEliminar(eq); setMenuEquipo(null); }}
                                                                    className="w-full px-5 py-3.5 text-left text-body-lg font-bold text-brand-red active:bg-[#FEE2E2] rounded-xl flex items-center gap-2.5">
                                                                    <LuTrash2 size={15} /> Eliminar
                                                                </button>
                                                    </ActionSheet>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {grande && !soloSede && sedesOrdenadas.length > cuantos && (
                        <button type="button" onClick={() => setCuantos(n => n + 15)}
                            className="w-full h-10 rounded-xl bg-chip text-ink text-label font-black active:scale-95">
                            Ver {Math.min(15, sedesOrdenadas.length - cuantos)} más · quedan {sedesOrdenadas.length - cuantos}
                        </button>
                    )}
                    {!grande && (
                        <button onClick={() => onAddSede(cliente)}
                            className="w-full py-2.5 rounded-xl text-label font-bold text-muted border border-dashed border-muted/30 active:scale-[0.98] transition-all">
                            Agregar o editar lugares
                        </button>
                    )}
                </div>

                {/* Resumen historial */}
                {serviciosCli.length > 0 && (
                    <button onClick={() => setModalHistorial(true)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-panel active:opacity-70 transition-opacity">
                        <span className="text-caption font-black text-brand-amber flex items-center gap-1">
                            <LuClipboardList size={13} />{serviciosCli.length} servicio{serviciosCli.length !== 1 ? 's' : ''} · último {formatFecha(ultimoServicio?.fecha)}
                        </span>
                        <span className="text-label text-muted">Ver →</span>
                    </button>
                )}
            </div>

            {/* Confirmación eliminar */}
            {confirmEliminar && (
                <>
                    <div className="fixed inset-0 bg-black/70 z-[199] backdrop-blur-sm" onClick={() => setConfirmEliminar(null)} />
                    <div className="fixed inset-0 flex items-center justify-center z-[200] p-4 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                        <div className="bg-card rounded-3xl w-full max-w-sm shadow-2xl p-6">
                            <div className="flex justify-center mb-3"><LuTriangleAlert size={28} className="text-brand-amber" /></div>
                            <h3 className="text-center text-body-lg font-black text-ink mb-2">
                                {confirmEliminar === 'cliente' ? 'Eliminar cliente' : `Eliminar equipo ${confirmEliminar.numeroSerie || ''}`}
                            </h3>
                            <p className="text-center text-caption text-muted mb-5">
                                {confirmEliminar === 'cliente' ? 'Esta acción no se puede deshacer' : 'Se borra el equipo y su historial de services. No se puede deshacer.'}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setConfirmEliminar(null)}
                                    className="flex-1 py-3 rounded-2xl font-black text-label uppercase bg-chip text-secondary active:scale-95">
                                    Cancelar
                                </button>
                                <button onClick={() => {
                                    if (confirmEliminar === 'cliente') onDeleteCliente(cliente.id);
                                    else onEliminarEquipoDefinitivo(confirmEliminar, { yaConfirmado: true });
                                    setConfirmEliminar(null);
                                }}
                                    className="flex-[2] py-3 rounded-2xl font-black text-label uppercase text-white bg-brand-red active:scale-95">
                                    Sí, eliminar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Modales */}
            {equipoHistorial && (
                <HistorialEquipoModal equipo={equipoHistorial} servicios={serviciosCli} onClose={() => setEquipoHistorial(null)} />
            )}
            {modalHistorial && (
                <HistorialClienteModal cliente={cliente} servicios={serviciosCli} onClose={() => setModalHistorial(false)} />
            )}
            {modalCierre && (
                <CierreMensualModal cliente={cliente} onClose={() => setModalCierre(false)} />
            )}
            {modalPortal && <FuncionesPortalModal cliente={cliente} onClose={() => setModalPortal(false)} />}
            {modalRentab && <RentabilidadCliente cliente={cliente} onCerrar={() => setModalRentab(false)} />}
            {pedido && <NuevoPedidoSheet comoEmpresa={{ id: cliente.id, nombre: cliente.nombre }} onCerrar={() => setPedido(false)}
                onCreado={() => setPedido(false)} />}
            {modalEmpresa && (
                <EmpresaPortalSheet cliente={cliente} onCerrar={() => setModalEmpresa(false)}
                    onTarifa={() => { setModalEmpresa(false); setModalCierre(true); }}
                    onFunciones={() => { setModalEmpresa(false); setModalPortal(true); }}
                    onRentabilidad={() => { setModalEmpresa(false); setModalRentab(true); }} />
            )}
        </div>
    );
}
